import { useState, useRef, useEffect, useMemo } from 'react';
import { MessageCircle, X, Send, UserRound } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { getVisitorSupabase } from '@/integrations/supabase/visitorClient';
import { toast } from 'sonner';
import { notifyTelegram } from '@/lib/notifyTelegram';

interface UIMessage {
  id: string;
  role: 'visitor' | 'ai' | 'admin';
  content: string;
}

const VISITOR_KEY = 'pacers_visitor_id';
const CONV_KEY = 'pacers_conv_id';
const NAME_KEY = 'pacers_visitor_name';

const WELCOME: UIMessage = {
  id: 'welcome',
  role: 'ai',
  content:
    "👋 Hi! I'm The Pacers Library assistant. Ask me about books, authors, pricing, or how we feature titles. If you'd rather speak to a person, tap **Talk to a human** below.",
};

export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<UIMessage[]>([WELCOME]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [visitorId, setVisitorId] = useState<string>('');
  const [visitorName, setVisitorName] = useState<string>('');
  const [nameInput, setNameInput] = useState('');
  const [escalated, setEscalated] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const isOpenRef = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => { isOpenRef.current = isOpen; if (isOpen) setUnreadCount(0); }, [isOpen]);

  const vsb = useMemo(() => (visitorId ? getVisitorSupabase(visitorId) : null), [visitorId]);

  useEffect(() => {
    let v = localStorage.getItem(VISITOR_KEY);
    if (!v) {
      v = crypto.randomUUID();
      localStorage.setItem(VISITOR_KEY, v);
    }
    setVisitorId(v);
    const n = localStorage.getItem(NAME_KEY);
    if (n) setVisitorName(n);
  }, []);

  useEffect(() => {
    if (!vsb) return;
    const existingConv = localStorage.getItem(CONV_KEY);
    if (existingConv) {
      setConversationId(existingConv);
      loadHistory(existingConv);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vsb]);

  const loadHistory = async (convId: string) => {
    if (!vsb) return;
    const { data: conv } = await vsb
      .from('chat_conversations')
      .select('escalated, visitor_name')
      .eq('id', convId)
      .maybeSingle();
    if (conv?.escalated) setEscalated(true);
    if (conv?.visitor_name && !visitorName) {
      setVisitorName(conv.visitor_name);
      localStorage.setItem(NAME_KEY, conv.visitor_name);
    }

    const { data: msgs } = await vsb
      .from('chat_messages')
      .select('id, role, content')
      .eq('conversation_id', convId)
      .order('created_at', { ascending: true });
    if (msgs && msgs.length) {
      setMessages([WELCOME, ...msgs.map((m: any) => ({ id: m.id, role: m.role, content: m.content }))]);
    }
  };

  useEffect(() => {
    if (!conversationId || !vsb) return;
    const channel = vsb
      .channel(`chat-${conversationId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'chat_messages', filter: `conversation_id=eq.${conversationId}` },
        (payload) => {
          const m = payload.new as any;
          setMessages((prev) => {
            if (prev.some((x) => x.id === m.id)) return prev;
            return [...prev, { id: m.id, role: m.role, content: m.content }];
          });
          if (m.role === 'admin' && !isOpenRef.current) {
            setUnreadCount((c) => c + 1);
            try {
              new Audio('data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=').play().catch(() => {});
            } catch {}
          }
        },
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'chat_conversations', filter: `id=eq.${conversationId}` },
        (payload) => {
          setEscalated(!!(payload.new as any).escalated);
        },
      )
      .subscribe();
    return () => { vsb.removeChannel(channel); };
  }, [conversationId, vsb]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const ensureConversation = async (nameOverride?: string): Promise<string | null> => {
    if (conversationId) return conversationId;
    if (!vsb) return null;
    const name = (nameOverride ?? visitorName).trim();
    const { data, error } = await vsb
      .from('chat_conversations')
      .insert({ visitor_id: visitorId, visitor_name: name || null })
      .select('id')
      .single();
    if (error || !data) {
      toast.error('Could not start conversation.');
      return null;
    }
    localStorage.setItem(CONV_KEY, data.id);
    setConversationId(data.id);
    return data.id;
  };

  const handleNameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const n = nameInput.trim();
    if (!n) return;
    setVisitorName(n);
    localStorage.setItem(NAME_KEY, n);
    setMessages((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        role: 'ai',
        content: `Nice to meet you, **${n}**! How can I help today?`,
      },
    ]);
    // If conversation already exists, update name
    if (conversationId && vsb) {
      vsb.from('chat_conversations').update({ visitor_name: n }).eq('id', conversationId);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || isSending || !vsb || !visitorName) return;

    setIsSending(true);
    const convId = await ensureConversation();
    if (!convId) { setIsSending(false); return; }

    const tempId = crypto.randomUUID();
    setMessages((prev) => [...prev, { id: tempId, role: 'visitor', content: text }]);
    setInput('');

    const { error: insertErr } = await vsb.from('chat_messages').insert({
      conversation_id: convId,
      visitor_id: visitorId,
      role: 'visitor',
      content: text,
    });
    if (insertErr) {
      toast.error('Failed to send.');
      setIsSending(false);
      return;
    }

    if (escalated) {
      setIsSending(false);
      return;
    }

    try {
      const { error: fnErr } = await supabase.functions.invoke('chat-ai', {
        body: { conversation_id: convId, visitor_id: visitorId, message: text },
      });
      if (fnErr) {
        toast.error('AI is unavailable. Try "Talk to a human".');
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsSending(false);
    }
  };

  const handleEscalate = async () => {
    if (!visitorName) {
      toast.error('Please enter your name first.');
      return;
    }
    const convId = await ensureConversation();
    if (!convId || !vsb) return;
    const { error } = await vsb
      .from('chat_conversations')
      .update({ escalated: true })
      .eq('id', convId);
    if (error) {
      toast.error('Could not escalate.');
      return;
    }
    setEscalated(true);
    notifyTelegram('New Live Chat', `${visitorName || 'Visitor'} requested a human on the live chat.`);
    toast.success('A team member will reply here shortly.');
    setMessages((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        role: 'ai',
        content: "✅ You're now connected to our team. Leave your question here and we'll reply in this chat.",
      },
    ]);
  };

  const needsName = !visitorName;

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground 
                   shadow-lg hover:bg-primary/90 transition-all duration-300 flex items-center justify-center
                   animate-fade-in"
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
        {!isOpen && unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-destructive text-destructive-foreground text-[11px] font-semibold flex items-center justify-center border-2 border-background animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-[340px] max-h-[560px] flex flex-col 
                        bg-card border border-border rounded-lg shadow-2xl animate-fade-in overflow-hidden">
          <div className="px-4 py-3 bg-primary text-primary-foreground flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle size={18} />
              <span className="font-serif text-sm tracking-wide">The Pacers Library</span>
            </div>
            <div className="flex items-center gap-2">
              {visitorName && (
                <span className="text-[10px] uppercase tracking-wider bg-primary-foreground/15 px-2 py-0.5 rounded">
                  {visitorName}
                </span>
              )}
              {escalated && (
                <span className="text-[10px] uppercase tracking-wider bg-primary-foreground/20 px-2 py-0.5 rounded">
                  Live
                </span>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[260px] max-h-[340px]">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex flex-col ${msg.role === 'visitor' ? 'items-end' : 'items-start'}`}
              >
                {msg.role === 'visitor' && visitorName && (
                  <span className="text-[10px] text-muted-foreground mb-0.5 mr-1">{visitorName}</span>
                )}
                <div
                  className={`max-w-[80%] px-3 py-2 rounded-lg text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.role === 'visitor'
                      ? 'bg-primary text-primary-foreground rounded-br-none'
                      : msg.role === 'admin'
                        ? 'bg-accent text-accent-foreground rounded-bl-none border border-primary/30'
                        : 'bg-secondary text-secondary-foreground rounded-bl-none'
                  }`}
                >
                  {msg.role === 'admin' && (
                    <div className="text-[10px] uppercase tracking-wider text-primary mb-1 font-semibold">
                      Pacers Team
                    </div>
                  )}
                  {msg.content}
                </div>
              </div>
            ))}
            {isSending && (
              <div className="flex justify-start">
                <div className="bg-secondary text-secondary-foreground px-3 py-2 rounded-lg rounded-bl-none text-sm">
                  <span className="animate-pulse">Typing…</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {needsName ? (
            <form
              onSubmit={handleNameSubmit}
              className="p-3 border-t border-border space-y-2 bg-secondary/30"
            >
              <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                <UserRound size={12} /> What's your name?
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  placeholder="Your name"
                  maxLength={60}
                  autoFocus
                  className="flex-1 bg-background text-foreground text-sm px-3 py-2 rounded-sm border border-border
                             placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <button
                  type="submit"
                  disabled={!nameInput.trim()}
                  className="px-3 py-2 bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 
                             transition-colors disabled:opacity-50 text-sm"
                >
                  Continue
                </button>
              </div>
            </form>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-border bg-secondary/30">
                <span className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                  {escalated ? <UserRound size={12} className="text-primary" /> : <MessageCircle size={12} />}
                  {escalated ? 'Chatting with the team' : 'AI assistant'}
                </span>
                <div className="flex items-center gap-2">
                  {!escalated && (
                    <button
                      type="button"
                      onClick={handleEscalate}
                      className="text-[11px] underline text-primary hover:text-primary/80 transition-colors"
                    >
                      Talk to a human
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleEscalate}
                    disabled={escalated}
                    role="switch"
                    aria-checked={escalated}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:cursor-default ${
                      escalated ? 'bg-primary' : 'bg-muted hover:bg-muted/80'
                    }`}
                    aria-label="Switch to a human"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-background shadow transition-transform ${
                        escalated ? 'translate-x-4' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <form
                onSubmit={(e) => { e.preventDefault(); handleSend(); }}
                className="flex items-center gap-2 p-3 border-t border-border"
              >
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={escalated ? 'Message the team…' : 'Ask me anything…'}
                  maxLength={1000}
                  className="flex-1 bg-secondary text-foreground text-sm px-3 py-2 rounded-sm border border-border
                             placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
                <button
                  type="submit"
                  disabled={isSending || !input.trim()}
                  className="p-2 bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 
                             transition-colors disabled:opacity-50"
                  aria-label="Send message"
                >
                  <Send size={16} />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
